import { FC } from 'react';

/**
 * CoCreationFlow — 共創のながれ（3起点合流型）
 *
 * 「どの役割からでも起点になれる」（SYSTEM_SPEC GEN-01）を図で表現する。
 * 直線STEP型（文章→絵→声）は起点が固定されて見えるため使わない（調停原則1）。
 * 記載内容はすべて実装済みの事実（lineage: BRW-02 / ギフト対等分配: INT-03）。
 */

const ORIGINS = [
    {
        tag: '物語が起点',
        strongText: '台本・詩・掌編から',
        example: '例: 磨いた台本に、絵と声が重なる',
        tagCls: 'bg-role-script-soft text-role-script-text',
        barCls: 'bg-role-script',
    },
    {
        tag: '絵が起点',
        strongText: '1枚のイラストから',
        example: '例: 描いた1枚に、物語と声が集まる',
        tagCls: 'bg-role-illustration-soft text-role-illustration-text',
        barCls: 'bg-role-illustration',
    },
    {
        tag: '声が起点',
        strongText: '朗読・即興ボイスから',
        example: '例: 声の録音に、脚本と絵が加わる',
        tagCls: 'bg-role-voice-soft text-role-voice-text',
        barCls: 'bg-role-voice',
    },
];

export const CoCreationFlow: FC = () => (
    <section className="w-full max-w-[1120px] mx-auto px-6 pt-12" aria-label="共創のながれ">
        <div className="text-center mb-6">
            <h2 className="font-display font-black text-ink text-[clamp(20px,2.5vw,25px)]">どの役割からでも、始められる</h2>
            <p className="mt-2 text-ink-2 text-sm">
                起点は物語でも、絵でも、声でもいい。互いに受け渡され、三位一体の作品として結晶します。
            </p>
        </div>

        {/* 3起点（表示ウェイト・文字量とも3役対等） */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {ORIGINS.map(o => (
                <div key={o.tag} className="relative text-center p-4 rounded-pop-md bg-surface border border-ink/10 shadow-sm text-[12px] overflow-hidden">
                    <span className={`absolute inset-x-0 top-0 h-1 ${o.barCls}`} aria-hidden />
                    <span className={`inline-block font-bold px-3 py-0.5 rounded-full mb-2 ${o.tagCls}`}>{o.tag}</span>
                    <strong className="block text-sm text-ink font-bold">{o.strongText}</strong>
                    <p className="mt-0.5 text-ink-2">{o.example}</p>
                </div>
            ))}
        </div>

        {/* 合流コネクタ: 3本の役割色ラインが中央へ */}
        <div className="flex justify-center gap-2 py-4" aria-hidden>
            <i className="w-[3px] h-7 rounded-sm bg-role-script rotate-[24deg]" />
            <i className="w-[3px] h-7 rounded-sm bg-role-illustration" />
            <i className="w-[3px] h-7 rounded-sm bg-role-voice -rotate-[24deg]" />
        </div>

        <div className="max-w-[520px] mx-auto text-center p-6 rounded-pop bg-surface border-2 border-ink shadow-hard">
            <span className="pop-tag mb-2">TRINITY</span>
            <h3 className="font-display font-black text-ink text-lg">三位一体の完成作品</h3>
            <p className="mt-2 text-[12px] text-ink-2">
                三役クレジットと系譜（Lineage）が刻まれ、応援ギフトは3役の貢献者に対等なルールで分配されます。
            </p>
        </div>
        <p className="text-center mt-4 text-[12px] text-ink-2">
            組み合わせは自由。<b className="text-ink">絵→声→物語</b>の順でも、<b className="text-ink">声→絵</b>の途中参加でも、系譜がすべての貢献を記録します。
        </p>
    </section>
);
